import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSession } from "@core/session/SessionProvider";
import { tokens } from "@core/theme/tokens";
import {
  Badge,
  Checkbox,
  DialogHeader,
  EmptyState,
  Field,
  inputStyle,
  Modal,
  PortalButton,
  useToast,
} from "@portal/common";
import { reportError } from "@portal/common/errorMessage";
import type { NotificationCampaignSummary } from "./api";
import { notificationCampaignSchema } from "./notificationSchemas";
import {
  useActiveMembers,
  useNotificationCampaign,
  useNotificationCampaigns,
  useNotificationMutations,
} from "./notificationQueries";

const statusLabel: Record<NotificationCampaignSummary["status"], string> = {
  sending: "Enviando",
  completed: "Completada",
  partial: "Parcial",
  failed: "Fallida",
};

function dateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("es-EC", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function statusTone(
  status: NotificationCampaignSummary["status"],
): "neutral" | "silver" | "achievement" | "danger" {
  if (status === "completed") return "achievement";
  if (status === "failed") return "danger";
  return status === "partial" ? "silver" : "neutral";
}

const audienceOptions = [
  {
    value: "all_active" as const,
    title: "Todos los miembros activos",
    description: "Incluye a todos los miembros con una cuenta activa.",
  },
  {
    value: "selected_members" as const,
    title: "Seleccionar miembros",
    description: "Elige manualmente quién recibirá esta comunicación.",
  },
];

export function NotificationsScreen() {
  const { session } = useSession();
  if (!session?.user.isAdministrator) return <Navigate to="/" replace />;
  return <NotificationsContent />;
}

function NotificationsContent() {
  const toast = useToast();
  const campaigns = useNotificationCampaigns();
  const mutations = useNotificationMutations();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<"title" | "body", string>>
  >({});
  const [scope, setScope] = useState<"all_active" | "selected_members">(
    "all_active",
  );
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null,
  );
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const members = useActiveMembers(search, scope === "selected_members");
  const detail = useNotificationCampaign(selectedCampaignId);
  const rows = campaigns.data?.items ?? [];

  const selectedCount = selected.size;
  const selectedSet = useMemo(() => selected, [selected]);

  function toggleMember(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function validateAndConfirm() {
    const parsed = notificationCampaignSchema.safeParse({ title, body });
    if (!parsed.success) {
      const nextErrors: Partial<Record<"title" | "body", string>> = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === "title" || field === "body") {
          nextErrors[field] = issue.message;
        }
      }
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});
    if (scope === "selected_members" && selectedCount === 0) {
      toast.show("Selecciona al menos un miembro activo", "neutral");
      return;
    }
    setConfirming(true);
  }

  function send() {
    const parsed = notificationCampaignSchema.parse({ title, body });
    const key = idempotencyKey ?? crypto.randomUUID();
    setIdempotencyKey(key);
    mutations.create.mutate(
      {
        input: {
          title: parsed.title,
          body: parsed.body,
          audience:
            scope === "all_active"
              ? { scope: "all_active" }
              : { scope: "selected_members", memberIds: [...selectedSet] },
        },
        idempotencyKey: key,
      },
      {
        onSuccess: (campaign) => {
          toast.show(`Campaña ${statusLabel[campaign.status].toLowerCase()}`);
          setConfirming(false);
          setIdempotencyKey(null);
          setTitle("");
          setBody("");
          setFieldErrors({});
          setSelected(new Set());
        },
        onError: (error) => reportError(toast, error),
      },
    );
  }

  return (
    <div>
      <header style={{ marginBottom: 26 }}>
        <h1
          className="display"
          style={{ margin: 0, color: tokens.text.primary, fontSize: 34 }}
        >
          Notificaciones push
        </h1>
        <p
          style={{
            color: tokens.text.tertiary,
            margin: "6px 0 0",
            fontSize: 13,
          }}
        >
          Envía noticias y beneficios comerciales a miembros activos.
        </p>
      </header>

      <section style={{ display: "grid", gap: 18 }}>
        <div style={panelStyle}>
          <h2 style={sectionTitle}>Nueva notificación</h2>
          <div style={formFieldsStyle}>
            <Field label="Título" error={fieldErrors.title}>
              <input
                style={inputStyle}
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setFieldErrors((current) => {
                    const next = { ...current };
                    delete next.title;
                    return next;
                  });
                }}
                maxLength={120}
              />
            </Field>
            <Field label="Mensaje" error={fieldErrors.body}>
              <textarea
                style={textareaStyle}
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                  setFieldErrors((current) => {
                    const next = { ...current };
                    delete next.body;
                    return next;
                  });
                }}
                rows={6}
                maxLength={1000}
              />
            </Field>
          </div>

          <fieldset style={audienceFieldsetStyle}>
            <legend style={labelStyle}>Audiencia</legend>
            <div style={audienceOptionsStyle}>
              {audienceOptions.map((option) => {
                const checked = scope === option.value;
                return (
                  <label
                    key={option.value}
                    style={audienceOptionStyle(checked)}
                  >
                    <input
                      type="radio"
                      name="notification-audience"
                      value={option.value}
                      checked={checked}
                      onChange={() => setScope(option.value)}
                      style={{
                        accentColor: tokens.text.primary,
                        flex: "none",
                        margin: "3px 0 0",
                      }}
                    />
                    <span style={audienceCopyStyle}>
                      <strong>{option.title}</strong>
                      <small>{option.description}</small>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          {scope === "selected_members" ? (
            <div style={memberPickerStyle}>
              <div style={memberPickerHeaderStyle}>
                <div>
                  <strong style={memberPickerTitleStyle}>
                    Miembros activos
                  </strong>
                  <span style={memberPickerCountStyle}>
                    {selectedCount} seleccionado
                    {selectedCount === 1 ? "" : "s"}
                  </span>
                </div>
                <span style={muted}>Máximo 20 resultados</span>
              </div>
              <input
                aria-label="Buscar miembros activos"
                placeholder="Buscar por nombre, correo o membresía"
                style={inputStyle}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className="portal-scroll" style={memberListStyle}>
                {members.isLoading ? <div style={muted}>Buscando…</div> : null}
                {members.data?.map((member) => (
                  <div
                    key={member.id}
                    style={memberRowStyle(selected.has(member.id))}
                  >
                    <Checkbox
                      checked={selected.has(member.id)}
                      onChange={() => toggleMember(member.id)}
                      ariaLabel={"Seleccionar a " + member.legalName}
                    />
                    <span style={memberIdentityStyle}>
                      <strong>{member.legalName}</strong>
                      <small>{member.email}</small>
                    </span>
                  </div>
                ))}
                {!members.isLoading && !members.data?.length ? (
                  <div style={muted}>
                    No hay miembros activos para esta búsqueda.
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <div style={composerFooterStyle}>
            <PortalButton
              type="button"
              size="small"
              onClick={validateAndConfirm}
              disabled={mutations.create.isPending}
            >
              Revisar y enviar
            </PortalButton>
          </div>
        </div>

        <div style={historyPanelStyle}>
          <h2 style={sectionTitle}>Historial reciente</h2>
          <p style={historyIntroStyle}>
            Consulta el estado y los destinatarios de tus últimas campañas.
          </p>
          {campaigns.isLoading ? (
            <div style={muted}>Cargando campañas…</div>
          ) : null}
          {campaigns.isError ? <EmptyState kind="error" /> : null}
          {!campaigns.isLoading && !campaigns.data?.items.length ? (
            <div style={muted}>Todavía no hay campañas enviadas.</div>
          ) : null}
          <div style={{ display: "grid", gap: 10 }}>
            {rows.map((campaign) => (
              <button
                key={campaign.id}
                type="button"
                onClick={() => setSelectedCampaignId(campaign.id)}
                style={historyRow}
                aria-label={"Ver detalle de " + campaign.title}
              >
                <span style={{ minWidth: 0, textAlign: "left" }}>
                  <strong
                    style={{
                      display: "block",
                      color: tokens.text.primary,
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {campaign.title}
                  </strong>
                  <small style={historyDateStyle}>
                    {dateTime(campaign.createdAt)}
                  </small>
                </span>
                <span style={{ display: "grid", justifyItems: "end", gap: 5 }}>
                  <Badge tone={statusTone(campaign.status)}>
                    {statusLabel[campaign.status]}
                  </Badge>
                  <small style={{ color: tokens.text.tertiary }}>
                    {campaign.counts.sent}/{campaign.counts.total} enviados
                  </small>
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {confirming ? (
        <Modal
          label="Confirmar envío"
          width={520}
          onClose={() => setConfirming(false)}
          header={
            <DialogHeader eyebrow="Comunicación" title="Confirmar envío" />
          }
          footer={
            <div style={modalActions}>
              <PortalButton
                type="button"
                tone="secondary"
                size="small"
                onClick={() => setConfirming(false)}
              >
                Cancelar
              </PortalButton>
              <PortalButton
                type="button"
                size="small"
                onClick={send}
                disabled={mutations.create.isPending}
              >
                {mutations.create.isPending ? "Enviando…" : "Enviar ahora"}
              </PortalButton>
            </div>
          }
        >
          <p style={modalIntroStyle}>
            Esta notificación se enviará inmediatamente a{" "}
            {scope === "all_active"
              ? "todos los miembros activos"
              : selectedCount + " miembros seleccionados"}
            .
          </p>
          <div style={previewStyle}>
            <strong>{title.trim()}</strong>
            <p style={previewBodyStyle}>{body.trim()}</p>
          </div>
        </Modal>
      ) : null}

      {selectedCampaignId && detail.data ? (
        <Modal
          label="Detalle de notificación"
          width={760}
          onClose={() => setSelectedCampaignId(null)}
          header={
            <DialogHeader
              eyebrow="Historial de campañas"
              title={detail.data.title}
            />
          }
        >
          <p style={modalIntroStyle}>{detail.data.body}</p>
          <div style={recipientSummaryStyle}>
            {detail.data.counts.sent} enviados ·{" "}
            {detail.data.counts.skippedNoDevice} sin dispositivo ·{" "}
            {detail.data.counts.failed} fallidos
          </div>
          <div className="portal-scroll" style={memberListStyle}>
            {detail.data.recipients.map((recipient) => (
              <div key={recipient.memberId} style={recipientRowStyle}>
                <span style={memberIdentityStyle}>
                  <strong>{recipient.memberName}</strong>
                  <small>{recipient.memberEmail}</small>
                </span>
                <Badge
                  tone={
                    recipient.status === "sent"
                      ? "achievement"
                      : recipient.status === "failed"
                        ? "danger"
                        : "neutral"
                  }
                >
                  {recipient.status}
                </Badge>
              </div>
            ))}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

const panelStyle = {
  border: `1px solid ${tokens.surface.line2}`,
  borderRadius: "18px",
  background: tokens.surface.bg2,
  padding: 26,
};
const historyPanelStyle = {
  ...panelStyle,
  padding: 24,
};
const sectionTitle = {
  margin: "0 0 22px",
  color: tokens.text.primary,
  font: `600 22px ${tokens.font.display}`,
};
const formFieldsStyle = {
  display: "grid",
  gap: 18,
};
const textareaStyle = {
  ...inputStyle,
  minHeight: 142,
  resize: "vertical" as const,
  lineHeight: 1.5,
};
const labelStyle = {
  color: tokens.text.secondary,
  font: `600 12px ${tokens.font.ui}`,
  marginBottom: 10,
};
const audienceFieldsetStyle = {
  border: 0,
  padding: 0,
  margin: "26px 0 0",
};
const audienceOptionsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 10,
};
const audienceOptionStyle = (checked: boolean) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 11,
  minHeight: 74,
  boxSizing: "border-box" as const,
  padding: "14px 15px",
  border: `1px solid ${checked ? tokens.surface.line2 : tokens.surface.line}`,
  borderRadius: tokens.radius.sm,
  background: checked ? tokens.surface.surface3 : tokens.surface.surface,
  cursor: "pointer",
});
const audienceCopyStyle = {
  display: "grid",
  gap: 5,
  color: tokens.text.primary,
  font: `500 13px ${tokens.font.ui}`,
};
const memberPickerStyle = {
  display: "grid",
  gap: 12,
  marginTop: 22,
};
const memberPickerHeaderStyle = {
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: 14,
  flexWrap: "wrap" as const,
};
const memberPickerTitleStyle = {
  display: "block",
  color: tokens.text.primary,
  font: `600 13px ${tokens.font.ui}`,
};
const memberPickerCountStyle = {
  display: "block",
  marginTop: 4,
  color: tokens.text.tertiary,
  font: `400 11.5px ${tokens.font.ui}`,
};
const memberListStyle = {
  display: "grid",
  gap: 4,
  maxHeight: 300,
  overflowY: "auto" as const,
  padding: 9,
  border: `1px solid ${tokens.surface.line}`,
  borderRadius: tokens.radius.sm,
  background: tokens.surface.surface,
};
const memberRowStyle = (selected: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "9px 7px",
  borderRadius: 10,
  background: selected ? tokens.surface.surface2 : "transparent",
});
const memberIdentityStyle = {
  display: "grid",
  gap: 3,
  minWidth: 0,
  color: tokens.text.secondary,
  font: `400 13px ${tokens.font.ui}`,
};
const historyIntroStyle = {
  margin: "-10px 0 20px",
  color: tokens.text.tertiary,
  font: `400 13px/1.5 ${tokens.font.ui}`,
};
const historyDateStyle = {
  display: "block",
  marginTop: 5,
  color: tokens.text.tertiary,
  font: `400 11.5px ${tokens.font.ui}`,
};
const historyRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  width: "100%",
  padding: "15px 16px",
  border: `1px solid ${tokens.surface.line}`,
  borderRadius: tokens.radius.sm,
  background: tokens.surface.surface,
  cursor: "pointer",
  color: tokens.text.secondary,
  textAlign: "left" as const,
  font: "inherit",
};
const previewStyle = {
  display: "grid",
  gap: 8,
  marginTop: 18,
  padding: 16,
  border: `1px solid ${tokens.surface.line}`,
  borderRadius: tokens.radius.sm,
  background: tokens.surface.surface,
  color: tokens.text.primary,
};
const previewBodyStyle = {
  margin: 0,
  color: tokens.text.secondary,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap" as const,
};
const modalIntroStyle = {
  margin: 0,
  color: tokens.text.secondary,
  fontSize: 13,
  lineHeight: 1.6,
};
const recipientSummaryStyle = {
  margin: "20px 0 14px",
  color: tokens.text.tertiary,
  fontSize: 13,
};
const recipientRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "10px 7px",
  borderBottom: `1px solid ${tokens.surface.line}`,
};
const composerFooterStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 26,
  paddingTop: 18,
  borderTop: `1px solid ${tokens.surface.line}`,
};
const modalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  flexWrap: "wrap" as const,
};
const muted = {
  color: tokens.text.tertiary,
  font: `400 13px ${tokens.font.ui}`,
};
